import { Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_QUOTATION_DETAIL, SUB_HEADER_QUOTATION_DETAIL } from "constants/data";
import { formatCurrency } from "utils";
import { Fragment } from "react/jsx-runtime";
import { useCallback } from "react";
import { QuoteTireDetail } from "features/quotation/types";

type QuotationDetailTableProps = {
  quotations: QuoteTireDetail[];
  selectedId: number[];
  onSelect(id: number, checked: boolean): void;
};

/**
 * QuotationDetailTable Component
 *
 * Renders a detailed table of quotation items, displaying two rows per item for various attributes and prices.
 * Supports selection of rows (currently commented out).
 * @param {QuotationDetailTableProps} props - The props for the component.
 */
export default function QuotationDetailTable({
  quotations,
  selectedId,
  onSelect,
}: QuotationDetailTableProps) {
  const bgCell = useCallback(
    (index: number) => (index % 2 !== 0 ? "var(--offwhite-300)" : "var(--white)"),
    [],
  );

  return (
    <TableContainer component={Table} sx={{ boxShadow: "none", width: "100%", minWidth: 1366 }}>
      <TableHead>
        <TableRowComponent data={HEADER_QUOTATION_DETAIL} type="header" />
        <TableRowComponent data={SUB_HEADER_QUOTATION_DETAIL} type="header" />
      </TableHead>
      <TableBody>
        {quotations.map((quotation, index) => (
          <Fragment key={quotation.quote_detail_id}>
            <TableRow
              sx={{
                backgroundColor: "var(--white)",
                height: "60px",
              }}
            >
              {/* <TableCellComponent rowSpan={2} sx={{ border: "1px solid var(--offwhite-500)" }}>
                <Checkbox
                  color="secondary"
                  inputProps={{
                    "aria-label": "select all desserts",
                  }}
                  checked={selectedId.includes(quotation.quote_detail_id)}
                  onChange={(event) => {
                    onSelect(quotation.quote_detail_id, event.target.checked);
                  }}
                />
              </TableCellComponent> */}
              <TableCellComponent
                sx={{
                  textAlign: "center",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.maker_name}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "center",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.tire_loin_speed_mark}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.quantity}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.special_discount ? `${quotation.special_discount}%` : `${quotation.fixed_discount || 0}%`}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.wage_cost_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.puncture_protection_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.cb_tire_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "left",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                  py: 1,
                  maxWidth: "200px",
                  wordBreak: "break-word",
                  whiteSpace: "normal",
                  overflow: "hidden",
                }}
                rowSpan={2}
              >
                {quotation.remarks}
              </TableCellComponent>
            </TableRow>
            <TableRow
              sx={{
                backgroundColor: "var(--white)",
                height: "60px",
              }}
            >
              <TableCellComponent
                sx={{
                  textAlign: "center",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.item_name}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "center",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {quotation.tire_label}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.nomal_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.discounted_sale_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.wage_cost_in_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.puncture_protection_in_price ?? 0)}
              </TableCellComponent>
              <TableCellComponent
                sx={{
                  textAlign: "right",
                  fontWeight: "bold",
                  border: "1px solid var(--offwhite-500)",
                  background: bgCell(index),
                }}
              >
                {formatCurrency(quotation.confirm_total_price ?? 0)}
              </TableCellComponent>
            </TableRow>
          </Fragment>
        ))}
      </TableBody>
    </TableContainer>
  );
}
